import React, { useState } from "react";
import { FormattedMessage } from "react-intl";
import { Grid, PageWrapper } from "components/Containers";
import { useAuth, useOrganization, useParamFunctions } from "hooks";
import { BannerShowcaseSignup } from "components/Banner";
import { css } from "@emotion/react";
import { MISSION_ROLES_COLUMNS } from "constants/index";
import { RolesFilters, RolesList, TalentSimpleRoleBoard, KEYS } from "components/Missions/Roles";
import { AIRoleBuilderButton, AIRoleBuilderTasksProvider } from "components/Missions/Roles/AIRoleBuilder";
import PageTitle from "components/PageTitle";
import { PrimaryLink } from "components/Links";

/**
 * Talent Simple Mission Board
 *
 * The Mission.dev client board, with the list view grouped into one paginated table per
 * role status. Card view is unchanged.
 */
const TalentSimpleMissionBoard = () => {
  const { paramParse, setParams } = useParamFunctions();
  const parsedFilters = paramParse(KEYS.filters);
  const { canViewUnpublishedMissions, organization, isTalentSimpleClient } = useAuth();
  const { data, loading: organizationLoading } = useOrganization({ id: organization?.slug });
  const { id: defaultMissionId } = data?.defaultMission || {};
  // Set initial view as List view for clients, Card view for showcase
  const [isCardView, setIsCardView] = useState(false);
  const publishFilter = { publishedMissions: !canViewUnpublishedMissions };
  const missionFilter = { includeDefaultMissions: isTalentSimpleClient };
  const filters = { ...parsedFilters, ...publishFilter, ...missionFilter };

  const handleViewChange = () => {
    setIsCardView(!isCardView);
    setParams({ [KEYS.filters]: { keyword: "" } });
  };

  const emptyMessageProps = {
    description: "Missions.Roles.RolesList.ClientEmpty",
  };

  const board = (
    <PageWrapper>
      <BannerShowcaseSignup
        css={css`
          margin: 4rem 0 1rem 0;
        `}
      />
      <Grid>
        <Grid.col start={1} end={13}>
          <PageTitle
            title={<FormattedMessage id="Missions.Roles.TalentSimple.H1" />}
            actionBar={
              <PrimaryLink onClick={handleViewChange} withMoreIcon>
                <FormattedMessage id={`Client.MissionBoard.SwitchViewButton.${isCardView ? "Card" : "List"}View`} />
              </PrimaryLink>
            }
          />
        </Grid.col>
        {!isCardView && (
          <Grid.col start={1} end={13}>
            <RolesFilters hideTalentOwnerFilter hideSalesOwnerFilter />
          </Grid.col>
        )}
        {isCardView ? (
          <Grid.col start={1} end={13}>
            <RolesList
              isCardView
              filters={filters}
              columns={COLUMNS}
              showAdvancedOptions={false}
              selectable={false}
              messageProps={emptyMessageProps}
              emptyMessage={emptyMessageProps}
            />
          </Grid.col>
        ) : (
          <TalentSimpleRoleBoard
            filters={filters}
            columns={COLUMNS}
            messageProps={emptyMessageProps}
            actionBars={ACTION_BARS(organization, defaultMissionId)}
          />
        )}
      </Grid>
    </PageWrapper>
  );

  if (isCardView) return board;

  return (
    <AIRoleBuilderTasksProvider missionId={defaultMissionId} loading={organizationLoading}>
      {board}
    </AIRoleBuilderTasksProvider>
  );
};

const COLUMNS = [
  MISSION_ROLES_COLUMNS.role,
  MISSION_ROLES_COLUMNS.skillsAndDisciplines,
  MISSION_ROLES_COLUMNS.status,
  MISSION_ROLES_COLUMNS.pipeline,
  MISSION_ROLES_COLUMNS.viewButton,
];

const ACTION_BARS = (organization, defaultMissionId) => ({
  drafts: (
    <AIRoleBuilderButton
      companyName={organization?.name}
      companyUrl={organization?.url}
      missionId={defaultMissionId}
      withRoleBuilder
    />
  ),
});

export default TalentSimpleMissionBoard;
